import { useMachine } from "@xstate/react";
import React, { useCallback, useMemo, useState } from "react";

import { Button } from "@lattice/frontend/components/kit/Button";
import { ListGroup, ListRow } from "@lattice/frontend/components/kit/List";
import { Surface } from "@lattice/frontend/components/kit/Surface";
import { TextField } from "@lattice/frontend/components/kit/TextField";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";
import { MixpanelEvents } from "@lattice/frontend/lib/analytics/track";
import {
  trackBrokerageConnected,
  trackBrokerageConnectFunnelEvent,
} from "@lattice/frontend/lib/analytics/brokerage-connect";

import { SettingsPanelError, SettingsPanelLoading } from "./SettingsPanelState";
import { SnapTradePortal } from "./SnapTradePortal";
import {
  webBrokerageConnectMachine,
  type T_WebBrokerage,
} from "./web-brokerage-connect-machine";

const BROKERAGE_ANALYTICS_SURFACE = "web_shell_settings";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  currency: "USD",
  maximumFractionDigits: 2,
  style: "currency",
});

export type T_BrokeragesOverviewAccount = {
  accountNumber: string;
  balance: number;
  cuid: string;
  nickname: string | null;
};

export type T_BrokeragesOverviewViewProps = {
  accounts: T_BrokeragesOverviewAccount[];
  disconnectError: string | null;
  disconnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
};

export function BrokeragesOverviewView({
  accounts,
  disconnectError,
  disconnecting,
  onConnect,
  onDisconnect,
}: T_BrokeragesOverviewViewProps) {
  const [disconnectConfirmationOpen, setDisconnectConfirmationOpen] =
    useState(false);

  return (
    <div className="shell-settings-form">
      {accounts.length === 0 ? (
        <Surface tone="subtle">
          <p className="shell-settings-delete__title">No brokerage connected</p>
          <p className="shell-settings__body">
            Connect a read-only account so Prism can understand your portfolio.
          </p>
        </Surface>
      ) : (
        <ListGroup>
          {accounts.map((account) => {
            const suffix = account.accountNumber.slice(-4);
            return (
              <ListRow
                key={account.cuid}
                subtitle={`${suffix ? `Account •${suffix} · ` : ""}${currencyFormatter.format(account.balance)}`}
                title={account.nickname ?? "Brokerage account"}
              />
            );
          })}
        </ListGroup>
      )}

      <Button
        label={accounts.length === 0 ? "Connect brokerage" : "Add brokerage"}
        onClick={onConnect}
      />

      {accounts.length > 0 ? (
        disconnectConfirmationOpen ? (
          <Surface tone="subtle">
            <p className="shell-settings-delete__title">
              Disconnect every brokerage?
            </p>
            <p className="shell-settings__body">
              Synced brokerage accounts and their imported data will be removed.
            </p>
            <div className="shell-settings-actions">
              <Button
                label="Keep connected"
                onClick={() => setDisconnectConfirmationOpen(false)}
                variant="secondary"
              />
              <Button
                label="Disconnect all"
                loading={disconnecting}
                onClick={onDisconnect}
                variant="danger"
              />
            </div>
          </Surface>
        ) : (
          <Button
            label="Disconnect all brokerages"
            onClick={() => setDisconnectConfirmationOpen(true)}
            variant="danger"
          />
        )
      ) : null}
      {disconnectError ? (
        <SettingsPanelError message={disconnectError} />
      ) : null}
    </div>
  );
}

export function BrokeragesPanel({
  initialConnectOpen = false,
}: {
  initialConnectOpen?: boolean;
}) {
  const [search, setSearch] = useState("");
  const utils = trpcApi.useUtils();
  const accounts = trpcApi.snaptrade.getAccounts.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 4 * 60 * 60 * 1000,
  });
  const brokerages = trpcApi.snaptrade.getSupportedBrokerages.useQuery(
    undefined,
    { refetchOnWindowFocus: false, staleTime: 24 * 60 * 60 * 1000 },
  );
  const getLoginLink = trpcApi.snaptrade.getLoginLink.useMutation();
  const syncConnection = trpcApi.snaptrade.handleConnection.useMutation();
  const disconnect = trpcApi.snaptrade.deleteUser.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.snaptrade.getAccounts.invalidate(),
        utils.snaptrade.getRegistrationStatus.invalidate(),
        utils.snaptrade.getAccountValueHistory.invalidate(),
      ]);
    },
  });

  const invalidatePortfolio = useCallback(async () => {
    await Promise.all([
      utils.snaptrade.getAccounts.invalidate(),
      utils.snaptrade.getRegistrationStatus.invalidate(),
      utils.snaptrade.getAccountValueHistory.invalidate(),
    ]);
  }, [utils]);
  const machineInput = useMemo(
    () => ({
      generateLoginLink: async (brokerage: T_WebBrokerage) => {
        const analyticsContext = {
          provider: brokerage.code,
          provider_name: brokerage.name,
          surface: BROKERAGE_ANALYTICS_SURFACE,
        };
        trackBrokerageConnectFunnelEvent(
          MixpanelEvents.BROKERAGE_CONNECT_LOGIN_LINK_REQUESTED,
          analyticsContext,
        );
        try {
          const result = await getLoginLink.mutateAsync({
            broker: brokerage.code,
            connectionType: "read",
            showCloseButton: true,
          });
          trackBrokerageConnectFunnelEvent(
            MixpanelEvents.BROKERAGE_CONNECT_LOGIN_LINK_SUCCEEDED,
            analyticsContext,
          );
          return result.redirectURI;
        } catch (error) {
          trackBrokerageConnectFunnelEvent(
            MixpanelEvents.BROKERAGE_CONNECT_LOGIN_LINK_FAILED,
            analyticsContext,
            { error_reason: "request_failed" },
          );
          throw error;
        }
      },
      onConnected: (brokerage: T_WebBrokerage) => {
        trackBrokerageConnected({
          provider: brokerage.code,
          provider_name: brokerage.name,
          surface: BROKERAGE_ANALYTICS_SURFACE,
        });
        void invalidatePortfolio();
      },
      startOpen: initialConnectOpen,
      syncConnection: async (
        authorizationId: string,
        brokerage: T_WebBrokerage,
      ) => {
        const analyticsContext = {
          provider: brokerage.code,
          provider_name: brokerage.name,
          surface: BROKERAGE_ANALYTICS_SURFACE,
        };
        trackBrokerageConnectFunnelEvent(
          MixpanelEvents.BROKERAGE_CONNECT_SYNC_STARTED,
          analyticsContext,
        );
        try {
          await syncConnection.mutateAsync({ authorizationId });
          trackBrokerageConnectFunnelEvent(
            MixpanelEvents.BROKERAGE_CONNECT_SYNC_SUCCEEDED,
            analyticsContext,
          );
          return { success: true as const };
        } catch (error) {
          trackBrokerageConnectFunnelEvent(
            MixpanelEvents.BROKERAGE_CONNECT_SYNC_FAILED,
            analyticsContext,
            { error_reason: "request_failed" },
          );
          throw error;
        }
      },
    }),
    [getLoginLink, initialConnectOpen, invalidatePortfolio, syncConnection],
  );
  const [snapshot, send] = useMachine(webBrokerageConnectMachine, {
    input: machineInput,
  });

  if (accounts.isPending || brokerages.isPending) {
    return <SettingsPanelLoading label="Loading brokerages" />;
  }
  if (accounts.isError || brokerages.isError || !brokerages.data) {
    return <SettingsPanelError message="Brokerages could not be loaded." />;
  }

  if (snapshot.matches("authorizing") && snapshot.context.loginLink) {
    return (
      <SnapTradePortal
        loginLink={snapshot.context.loginLink}
        onCancel={() => send({ type: "PORTAL_CANCELLED" })}
        onError={(message) => send({ message, type: "PORTAL_ERROR" })}
        onSuccess={(authorizationId) =>
          send({ authorizationId, type: "AUTHORIZED" })
        }
      />
    );
  }

  if (snapshot.matches("selecting") || snapshot.matches("generatingLink")) {
    const normalizedSearch = search.trim().toLowerCase();
    const visibleBrokerages = brokerages.data.filter((brokerage) =>
      `${brokerage.name} ${brokerage.code}`
        .toLowerCase()
        .includes(normalizedSearch),
    );
    return (
      <div className="shell-settings-form">
        <TextField
          label="Choose a brokerage"
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search brokerages"
          type="search"
          value={search}
        />
        <ListGroup>
          {visibleBrokerages.map((brokerage) => (
            <ListRow
              disabled={snapshot.matches("generatingLink")}
              key={brokerage.code}
              leading={
                <img
                  alt=""
                  className="shell-settings-brokerage__logo"
                  src={brokerage.logoUrl}
                />
              }
              onClick={() => {
                trackBrokerageConnectFunnelEvent(
                  MixpanelEvents.BROKERAGE_CONNECT_BROKERAGE_SELECTED,
                  {
                    provider: brokerage.code,
                    provider_name: brokerage.name,
                    surface: BROKERAGE_ANALYTICS_SURFACE,
                  },
                );
                send({ brokerage, type: "SELECT_BROKERAGE" });
              }}
              subtitle="Read-only portfolio connection"
              title={brokerage.name}
            />
          ))}
        </ListGroup>
        <Button
          label="Cancel"
          onClick={() => send({ type: "CLOSE_CONNECT" })}
          variant="quiet"
        />
      </div>
    );
  }

  if (snapshot.matches("syncing")) {
    return <SettingsPanelLoading label="Syncing your portfolio" />;
  }
  if (snapshot.matches("failed")) {
    return (
      <div className="shell-settings-form">
        <SettingsPanelError
          message={snapshot.context.error ?? "Brokerage connection failed."}
        />
        <Button label="Try again" onClick={() => send({ type: "RETRY" })} />
        <Button
          label="Cancel"
          onClick={() => send({ type: "CLOSE_CONNECT" })}
          variant="quiet"
        />
      </div>
    );
  }
  if (snapshot.matches("connected")) {
    return (
      <div className="shell-settings-form">
        <Surface tone="accent">
          <p className="shell-settings-delete__title">Brokerage connected</p>
          <p className="shell-settings__body">
            Your portfolio data is syncing into Lattice.
          </p>
        </Surface>
        <Button label="Done" onClick={() => send({ type: "CLOSE_CONNECT" })} />
      </div>
    );
  }

  const connectedAccounts = (accounts.data?.accounts ?? []).filter(
    (account) => account.snaptrade_account_id !== null,
  );

  return (
    <BrokeragesOverviewView
      accounts={connectedAccounts.map((account) => ({
        accountNumber: account.account_number,
        balance: account.balance,
        cuid: account.cuid,
        nickname: account.nickname,
      }))}
      disconnectError={disconnect.error?.message ?? null}
      disconnecting={disconnect.isPending}
      onConnect={() => {
        trackBrokerageConnectFunnelEvent(
          MixpanelEvents.BROKERAGE_CONNECT_CTA_TAPPED,
          { surface: BROKERAGE_ANALYTICS_SURFACE },
          { cta_location: "settings_brokerages_panel" },
        );
        send({ type: "OPEN_CONNECT" });
      }}
      onDisconnect={() => disconnect.mutate()}
    />
  );
}
